// Ordered surface-layer chain for the terrain material.
//
// The terrain shader computes a base albedo (biomes, rock, snow, brush strokes), then hands it to
// each registered layer in ascending `order`. A layer returns the new albedo and may add emissive
// light or change roughness. Borders, lenses and fog plug in here without editing terrain code.
//
//   base albedo ─► layer(order 0) ─► layer(10) ─► … ─► colorNode ─┐
//                        └──────── emissive sum ─────► emissiveNode├─► painted lighting
//                        └──────── roughness ────────► roughnessNode┘
//
// Layers run once per material build, not per frame: they return TSL node graphs. Registering or
// removing a layer rebuilds the terrain material.

import type Node from 'three/src/nodes/core/Node.js';
import type { HexData } from './hexdata.ts';
import type { HexFrag } from './tsl/hex.ts';

type F = Node<'float'>;
type V3 = Node<'vec3'>;

/** What a layer can read about the fragment. All values are TSL nodes. */
export interface SurfaceCtx {
  /** The fragment's hex: col/row, texel coord, centre, local offset, distance to each edge. */
  hex: HexFrag;
  /** World position (x unwrapped: it includes the ±WORLD_W copy shift). */
  world: Node<'vec3'>;
  /** World XZ with x wrapped into [0, WORLD_W): use this for anything tied to the map. */
  xz: Node<'vec2'>;
  /** World-space geometry normal (before the terrain's bump detail). */
  normal: V3;
  /** Pixel footprint in world units: fade any detail finer than a few of these. */
  px: F;
  /** Albedo produced by the layers before this one (linear RGB). */
  color: V3;
  /** Terrain albedo before any layer ran. */
  base: V3;
  /** Roughness so far. */
  roughness: F;
  /** Per-hex data textures and their decoders. */
  data: HexData;
  /** Seconds since the view started. */
  time: F;
  /** Camera height above the ground in hex radii (fade detail with zoom). */
  camHeight: F;
}

export interface SurfaceLayer {
  name: string;
  /** Ascending; built-ins use 0 (hex edges), 100 (lakes), 110 (rivers), 900 (highlight). */
  order: number;
  color?: (ctx: SurfaceCtx) => V3;
  emissive?: (ctx: SurfaceCtx) => V3;
  roughness?: (ctx: SurfaceCtx) => F;
  /**
   * 'props': the layer is also evaluated on mountain peaks (features/layer.ts), so its lines stay
   * unbroken where relief stands over them. Only lines tied to hex edges belong here.
   */
  drape?: 'props';
}

// Layers registered before `drape` existed (political/, not editable from the view module).
const DRAPED_NAMES: ReadonlySet<string> = new Set(['borders', 'fronts', 'cityStates', 'highlight', 'hover']);

/** Whether a layer paints the peaks too (its `drape` flag, or a built-in political/overlay name). */
export function drapesOnProps(layer: SurfaceLayer): boolean {
  return layer.drape === 'props' || DRAPED_NAMES.has(layer.name);
}

/** Registry of surface layers; the terrain material listens and rebuilds on change. */
export class SurfaceChain {
  private layers: SurfaceLayer[] = [];
  private listeners = new Set<() => void>();

  /** Add a layer; returns a function that removes it. A layer name replaces an earlier one. */
  register(layer: SurfaceLayer): () => void {
    this.layers = this.layers.filter((l) => l.name !== layer.name);
    this.layers.push(layer);
    this.layers.sort((a, b) => a.order - b.order);
    this.notify();
    return () => this.remove(layer.name);
  }

  remove(name: string): void {
    const before = this.layers.length;
    this.layers = this.layers.filter((l) => l.name !== name);
    if (this.layers.length !== before) {
      this.notify();
    }
  }

  list(): readonly SurfaceLayer[] {
    return this.layers;
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private notify(): void {
    for (const cb of this.listeners) {
      cb();
    }
  }
}
